import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import "./index.scss";
import { Button, Row, Col, Select, Form, Radio, DatePicker, Input, Checkbox, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import ENVIRONMENT from "../../../../environments";
import axios from "axios";
import ContractConstants from "./ContractConstants";

const { Option } = Select;

const ContractForm = forwardRef((props, ref) => {

    const [customerList, setCustomerList] = useState([]);
    const [companyList, setCompanyList] = useState([]);
    const [companyLoader, setCompanyLoader] = useState(false);
    const [customerLoader, setCustomerLoader] = useState(false);
    const [contractType, setContractType] = useState('annual');
    const [userType, setUserType] = useState('existing_user');
    
    const [form] = Form.useForm();

    useEffect(() => {
        if(props.contractFormInfo && Object.keys(props.contractFormInfo).length > 0){
            if(props.contractFormInfo.contract_type != 'annual')
                setContractType('project')
        }

        let company_id = new URLSearchParams(window.location.search).get("company_id")
        let company_name = new URLSearchParams(window.location.search).get("company_name")
        if(company_id && company_name){
            form.setFieldsValue({ company_name: company_name });
            form.setFieldsValue({ company_id: company_id });
        }
        validateForm();
    }, []);

    // Update form fields when contractFormInfo changes (e.g., when navigating back)
    useEffect(() => {
        if(props.contractFormInfo && Object.keys(props.contractFormInfo).length > 0){
            // Update userType state to match contractFormInfo
            if(props.contractFormInfo.user_type){
                setUserType(props.contractFormInfo.user_type);
            }
            
            // Update contractType state
            if(props.contractFormInfo.contract_type){
                setContractType(props.contractFormInfo.contract_type === 'project' ? 'project' : 'annual');
            }
            
            // Update all form fields from contractFormInfo
            form.setFieldsValue(props.contractFormInfo);
        }
    }, [props.contractFormInfo]);

    const getCompanyList = async () => {
        setCompanyLoader(true)
        axios.post(ENVIRONMENT.COMPANY_GET_BATCH, {})
            .then(res => {
                var data = [];
                for (let i = 0; i < (res.data).length; i++){
                    if(res.data[i]['company_display_name'] != ""){
                        data.push({
                            key: i,
                            company_name: res.data[i]['company_display_name'],
                            company_id: res.data[i]['company_id']
                        })
                    }
                }
                data = data.sort(function (a, b) {
                    return a.company_name.toLowerCase().localeCompare(b.company_name.toLowerCase());
                });
                setCompanyList(data);
                setCompanyLoader(false);
            })
    }
    const getCustomerList = async () => {
        setCustomerLoader(true);
        let allEmails = [];
        let keyCounter = 1;
        
        try {
            const [batchRes, invitedRes] = await Promise.all([
                axios.post(ENVIRONMENT.CUSTOMER_GET_BATCH, {}),
                axios.post(ENVIRONMENT.CUSTOMER_INVITED, {})
            ]);
            
            // Get emails from CUSTOMER_GET_BATCH
            for (let i = 0; i < batchRes.data.length; i++) {
                allEmails.push({
                    key: keyCounter++,
                    email: batchRes.data[i]['email'],
                });
            }
            
            // Get emails from CUSTOMER_INVITED
            if (invitedRes.data && Array.isArray(invitedRes.data)) {
                for (let i = 0; i < invitedRes.data.length; i++) {
                    if (invitedRes.data[i]['customer_email']) {
                        allEmails.push({
                            key: keyCounter++,
                            email: invitedRes.data[i]['customer_email'],
                        });
                    }
                }
            }
            
            setCustomerList(allEmails);
        } catch (error) {
            console.error('Error fetching customer list:', error);
        } finally {
            setCustomerLoader(false);
        }
    }

    useEffect(() => {
        getCustomerList();
        getCompanyList();
    }, []);


    const onChangeContract = (e) => {
        setContractType(e.target.value);
    }

    const onChangeUserType = (e) => {
        setUserType(e.target.value);
    }

    const validateEmailDuplicate = (email) => {
        if (!email || (userType !== 'new_user' && userType !== 'test_user')) {
            return;
        }
        
        const emailExists = customerList.some(
            customer => customer.email && 
            customer.email.toLowerCase() === email.toLowerCase()
        );
        
        if (emailExists) {
            form.setFields([
                {
                    name: 'new_user_email',
                    errors: ['This email is already in use. Please use a different email address.'],
                },
            ]);
        } else {
            // Clear the duplicate error if email doesn't exist
            const emailFieldErrors = form.getFieldsError(['new_user_email']);
            if (emailFieldErrors[0] && emailFieldErrors[0].errors.length > 0) {
                const hasDuplicateError = emailFieldErrors[0].errors.some(
                    err => err.includes('already in use')
                );
                if (hasDuplicateError) {
                    form.setFields([
                        {
                            name: 'new_user_email',
                            errors: [],
                        },
                    ]);
                }
            }
        }
        
        // Trigger validation to update button state
        validateForm();
    }

    const finishForm = (values) => {
        props.setContractFormInfo(values);
    }

    const validateForm = async () => {
        let formValues = form.getFieldsValue();
        let filteredValues = { ...formValues };

        if(filteredValues.contract_type !== 'annual'){
            delete filteredValues.payment_schedule;
            delete filteredValues.renewal_notice;
            delete filteredValues.usage_type;
        }

        if(filteredValues.user_type === 'existing_user'){
            delete filteredValues.new_user_email;
        } else {
            delete filteredValues.company_name;
            delete filteredValues.company_id;
        }

        const allFieldsModified = Object.values(filteredValues).every(val => val !== undefined && val !== null && val !== "");
        
        // Check email validation and duplicate for new_user or test_user
        let emailValid = true;
        if ((userType === 'new_user' || userType === 'test_user') && filteredValues.new_user_email) {
            // Check for form validation errors
            const emailFieldErrors = form.getFieldsError(['new_user_email']);
            if (emailFieldErrors[0] && emailFieldErrors[0].errors.length > 0) {
                emailValid = false;
            } else {
                // Check if email already exists in customerList
                const emailExists = customerList.some(
                    customer => customer.email && 
                    customer.email.toLowerCase() === filteredValues.new_user_email.toLowerCase()
                );
                if (emailExists) {
                    emailValid = false;
                    // Set error on the field
                    form.setFields([
                        {
                            name: 'new_user_email',
                            errors: ['This email is already in use. Please use a different email address.'],
                        },
                    ]);
                }
            }
        }
        
        props.setButtonDisabledState(!allFieldsModified || !emailValid);
    };
    

    useImperativeHandle(ref, () => ({
        submit: async () => {
            try {
                const values = await form.validateFields();
                
                // Check if email already exists in customerList for new_user or test_user
                if ((userType === 'new_user' || userType === 'test_user') && values.new_user_email) {
                    const emailExists = customerList.some(
                        customer => customer.email && 
                        customer.email.toLowerCase() === values.new_user_email.toLowerCase()
                    );
                    
                    if (emailExists) {
                        form.setFields([
                            {
                                name: 'new_user_email',
                                errors: ['This email is already in use. Please use a different email address.'],
                            },
                        ]);
                        throw new Error('Email already exists in customer list');
                    }
                }
                
                finishForm(values);
                return values;
            } catch (error) {
                throw error;
            }
        }
    }));
    

    return (
        <Row className="mb-60">
            <Col span={6} />
            <Col span={13} className="justify-in-center">
                <Row style={{ width: '100%'}}>
                    <Col span={24}>
                        <div className="manrope f-24 black-33 w-700 mt-40 mb-30">Create New Contract</div>
                    </Col>
                    <Col span={24} className="contract-frm-settings">
                        <Form layout="vertical"
                            onFinish={finishForm}
                            initialValues={
                                props.contractFormInfo && Object.keys(props.contractFormInfo).length > 0
                                ? props.contractFormInfo
                                : { contract_type: 'annual', usage_type: 'monthly', payment_schedule: 'monthly', renewal_notice: '30', user_type: 'existing_user' }
                            }
                            className="contract-form"
                            form={form}
                            onValuesChange={validateForm}>
                                <Col span={24} style={{width: '70%'}}>
                                <Col span={24} className="mgt-20">
                                    <Form.Item name="user_type">
                                        <Radio.Group className="contract-radio-btn" value={userType} onChange={onChangeUserType}>
                                            <Radio value="existing_user" className="manrope f-14 black-99"> 
                                                For existing company 
                                                <Tooltip title={<span className='manrope f-12 white'>Select this option for companies that already exist in the system. You will be able to choose from a list of existing companies.</span>}>
                                                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#276DD7', cursor: 'pointer' }} />
                                                </Tooltip>
                                            </Radio>
                                            <Radio value="new_user" className="manrope f-14 black-99"> 
                                                For new company
                                                <Tooltip title={<span className='manrope f-12 white'>Select this option for new companies that need to be created. You will need to provide an email address for the new company.</span>}>
                                                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#276DD7', cursor: 'pointer' }} />
                                                </Tooltip>
                                            </Radio>
                                            <Radio value="test_user" className="manrope f-14 black-99"> 
                                                For potential lead
                                                <Tooltip title={<span className='manrope f-12 white'>Select this option for potential leads or test contracts. This will use default pricing values and skip the detailed pricing configuration step.</span>}>
                                                    <InfoCircleOutlined style={{ marginLeft: 8, color: '#276DD7', cursor: 'pointer' }} />
                                                </Tooltip>
                                            </Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                </Col>
                                {userType === 'existing_user' ? (
                                    <Form.Item label={<span className="manrope f-16 w-700 black-00">Customer</span>} name="company_name" >
                                        <Select
                                            placeholder="Select customer"
                                            className='manrope f-14 w-400 placeholder black'
                                            showSearch
                                            filterOption={(input, option) =>
                                                option.children.toLowerCase().includes(input.toLowerCase())
                                            }
                                            onChange={(value) => {
                                                const selectedCompany = companyList.find(company => company.company_id === value);
                                                console.log(selectedCompany)
                                                form.setFieldsValue({ 
                                                    company_name: selectedCompany?.company_name,
                                                    company_id: selectedCompany?.company_id 
                                                });
                                                form.validateFields(["company_name"]);
                                            }}
                                            notFoundContent={companyLoader ? "Loading Data..." : "No companies available"}
                                            disabled={props.action == 'edit'}>
                                            {companyList.map((item, index) => {
                                                return (
                                                    <Option className='manrope f-14 w-400' key={item.company_id} value={item.company_id}>
                                                        {item.company_name}
                                                    </Option>
                                                );
                                            })}
                                        </Select>
                                    </Form.Item>
                                ) : (
                                    <>
                                        <Form.Item 
                                            label={<span className="manrope f-16 w-700 black-00">Customer</span>} 
                                            name="new_user_email"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'Please enter an email address',
                                                },
                                                {
                                                    type: 'email',
                                                    message: 'Please enter a valid email address',
                                                },
                                            ]}
                                        >
                                            <Input
                                                className='manrope f-14 w-400 placeholder black'
                                                placeholder="Enter email"
                                                onChange={(e) => {
                                                    const email = e.target.value;
                                                    // Validate email format first, then check for duplicates
                                                    setTimeout(() => {
                                                        validateEmailDuplicate(email);
                                                    }, 100);
                                                }}
                                            />
                                        </Form.Item>
                                    </>
                                )}
                            </Col>
                            <hr className="mb-30 mt-30" />
                            <Col span={24}>
                                <p className="manrope f-16 w-700 black-00">Contract Details</p>
                            </Col>
                            <Col span={24} className="mgt-20">
                                <Form.Item label={<span className="manrope f-14 w-700 black-00">Select contract type</span>} name="contract_type">
                                    <Radio.Group className="contract-radio-btn" value={contractType} onChange={onChangeContract}>
                                        <Radio value="annual" className="manrope f-14 black-99"> Annual </Radio>
                                        <Radio value="project" className="manrope f-14 black-99"> Project <span className="manrope f-14 grey-73">&nbsp;Includes POC or Test</span></Radio>
                                    </Radio.Group>
                                </Form.Item>
                            </Col>
                            {contractType === "annual" ?
                                <>
                                    <Col span={24}>
                                        <Form.Item
                                            label={<span className="manrope f-14 w-700 black-00 justify-space-between"><span>Set usage</span> <span className="manrope f-14 grey-73">This determines how credits are distributed throughout the year.</span></span>} name="usage_type">
                                            <Radio.Group className="contract-radio-btn">
                                                <Radio value="monthly" className="manrope f-14 black-99"> Monthly </Radio>
                                                <Radio value="quarterly" className="manrope f-14 black-99"> Quarterly </Radio>
                                                <Radio value="6_months" className="manrope f-14 black-99"> Every 6 months </Radio>
                                                <Radio value="annually" className="manrope f-14 black-99"> Annually </Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </Col>

                                    <Col span={24} className="mb-20">
                                        <Form.Item label={<span className="manrope f-14 w-700 black-00">Set contract start date</span>} name="contract_start_date" >
                                            <DatePicker placeholder="Select date" className="manrope f-14 black-00" style={{width: '30%'}}/>
                                        </Form.Item>
                                    </Col>

                                    <Col span={24} className="mb-20">
                                        <Form.Item label={<span className="manrope f-14 w-700 black-00">Set contract end date</span>} name="contract_end_date" >
                                            <DatePicker placeholder="Select date" className="manrope f-14 black-00" style={{width: '30%'}}/>
                                        </Form.Item>
                                    </Col>

                                    <Col span={24} className="mb-20">   
                                    <span className="manrope f-14 black-99 renewal-notice-text" >
                                        <span>Renewal Notice will go out </span>
                                        <Form.Item name="renewal_notice" className="no-margin">
                                                <Select defaultValue="30" className="manrope f-14 black-99">
                                                    <Select.Option value="30">30 Days</Select.Option>
                                                    <Select.Option value="60">60 Days</Select.Option>
                                                    <Select.Option value="90">90 Days</Select.Option>
                                                </Select>
                                        </Form.Item>
                                        <span> before contract ends</span>
                                    </span>
                                    </Col>

                                    <Col span={24}>
                                        <Form.Item
                                            label={<span className="manrope f-14 w-700 black-00 justify-space-between"><span>Set payment schedule</span> <span className="manrope f-14 grey-73">Billed at beginning of each payment period.</span></span>}
                                            name="payment_schedule">
                                            <Radio.Group className="contract-radio-btn"> 
                                                <Radio value="monthly" className="manrope f-14 black-99"> Monthly </Radio>
                                                <Radio value="quarterly" className="manrope f-14 black-99"> Quarterly </Radio>
                                                <Radio value="6_months" className="manrope f-14 black-99"> Every 6 months </Radio>
                                                <Radio value="annually" className="manrope f-14 black-99"> Annually </Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </Col>
                                </> : 
                                <>
                                <Col span={24} className="mb-20">
                                    <Form.Item label={<span className="manrope f-14 w-700 black-00">Set contract start date</span>} name="contract_start_date" >
                                        <DatePicker placeholder="Select date" className="manrope f-14 black-00" style={{width: '30%'}}/>
                                    </Form.Item>
                                </Col>
                                <Col span={24} className="mb-20">
                                    <Form.Item label={<span className="manrope f-14 w-700 black-00">Set contract end date</span>} name="contract_end_date" >
                                        <DatePicker placeholder="Select date" className="manrope f-14 black-00" style={{width: '30%'}}/>
                                    </Form.Item>
                                </Col>
                                </>
                                }
                            <hr className="mb-30 mt-30" />

                            <Col span={24}>
                                <p className="manrope f-16 w-700 black-00 mb-20">Account Executive Info</p>
                            </Col>
                            <Form.Item name="account_exe_username" style={{width: '70%'}}>
                                <Select
                                    placeholder="Select employee"
                                    className='manrope f-14 w-400 placeholder black'
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={async (value) => {
                                        const selectedUser = ContractConstants.ACCOUNT_EXECUTIVE_USERS.find(customer => customer.customer_username === value);
                                        form.setFieldsValue({ account_exe_username: selectedUser?.customer_username,account_exe_email: selectedUser?.email });
                                        await form.validateFields(["account_exe_email"]); 
                                        validateForm();
                                    }}
                                    notFoundContent={customerLoader ? "Loading Data..." : "No customers available"}>
                                    {ContractConstants.ACCOUNT_EXECUTIVE_USERS.map((item, index) => {
                                        return (
                                            <Option className='manrope f-14 w-400' key={item.customer_username} value={item.customer_username}>
                                                {item.customer_username}
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </Form.Item>
                            <Form.Item name="account_exe_email" style={{width: '70%'}}>
                                <Input
                                    className='manrope f-14 w-400 placeholder'
                                    placeholder="Email"
                                />
                            </Form.Item>

                            <Col span={24}>
                                <p className="manrope f-16 w-700 black-00 mb-20 mt-30">Customer Success Info</p>
                            </Col>
                            <Form.Item name="customer_success_username" style={{width: '70%'}}>
                                <Select
                                    placeholder="Select employee"
                                    className='manrope f-14 w-400 placeholder black'
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().includes(input.toLowerCase())
                                    }
                                    onChange={async (value) => {
                                        const selectedUser = ContractConstants.CUSTOMER_SUCCESS_USERS.find(customer => customer.customer_username === value);
                                        form.setFieldsValue({  customer_success_username: selectedUser?.customer_username, customer_success_email: selectedUser?.email });
                                        await form.validateFields(["customer_success_email"]);
                                        validateForm();
                                    }}
                                    notFoundContent={customerLoader ? "Loading Data..." : "No customers available"}>
                                    {ContractConstants.CUSTOMER_SUCCESS_USERS.map((item, index) => {
                                        return (
                                            <Option className='manrope f-14 w-400' key={item.customer_username} value={item.customer_username}>
                                                {item.customer_username}
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </Form.Item>
                            <Form.Item name="customer_success_email" style={{width: '70%', paddingBottom: 50}}>
                                <Input
                                    className='manrope f-14 w-400 placeholder'
                                    placeholder="Email"
                                />
                            </Form.Item>
                            <Form.Item name="company_id" hidden>
                                <Input />
                            </Form.Item>
                        </Form>
                    </Col>
                </Row>
            </Col>
            <Col span={5} />
        </Row>
    );

})

export default ContractForm;